import { useEffect, useState } from 'react';

import { notificationsTopic } from '../../api/notifications';
import { realtime } from '../../api/runs';

/**
 * The live half of the notification centre.
 *
 * Same shape as `useConversationStream` and for the same reason: the stream
 * is an optimisation, not the source of truth. One EventSource for the view
 * (`13 §5`) carrying every company's `/notifications` topic; each frame just
 * asks the caller to refetch — the payload does carry the notification, but
 * splicing it into a filtered, paginated cache is exactly the kind of
 * client-side re-derivation that goes subtly wrong, and one refetch is cheap.
 *
 * On reconnect after a cut, the caller is asked to refetch too: SSE does not
 * replay what was missed.
 */

const RECONNECT_MS = 3_000;

export function useNotificationsStream(companyIds: string[], onNotification: () => void): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const topicsKey = companyIds.join(',');

  useEffect(() => {
    const ids = topicsKey === '' ? [] : topicsKey.split(',');

    if (ids.length === 0) {
      setConnected(false);

      return;
    }

    let source: EventSource | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let everConnected = false;

    const open = async (): Promise<void> => {
      try {
        const grant = await realtime.grant();

        if (stopped) return;

        const url = new URL(grant.hub_url, window.location.origin);

        for (const id of ids) url.searchParams.append('topic', notificationsTopic(id));

        source = new EventSource(url.toString(), { withCredentials: true });

        source.onopen = () => {
          setConnected(true);

          // A reconnect means frames may have been lost while down.
          if (everConnected) onNotification();
          everConnected = true;
        };

        source.onmessage = (event: MessageEvent<string>) => {
          try {
            const payload = JSON.parse(event.data) as { type?: string };

            if (payload.type === 'notification') onNotification();
          } catch {
            // A frame this client cannot read is not a reason to tear the
            // stream down: the next refetch has everything anyway.
          }
        };

        source.onerror = () => {
          setConnected(false);
          source?.close();
          source = null;

          if (!stopped) reconnect = setTimeout(() => void open(), RECONNECT_MS);
        };
      } catch {
        setConnected(false);
      }
    };

    void open();

    return () => {
      stopped = true;
      if (reconnect !== null) clearTimeout(reconnect);
      source?.close();
      setConnected(false);
    };
  }, [topicsKey, onNotification]);

  return { connected };
}
